import { useMemo, useState } from 'react';
import {
  Plus, Search, ListOrdered, AlertCircle, MoreVertical, Pencil,
  CheckCircle2, RotateCcw, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { can } from '@/types';
import {
  useJournal, usePostJournalEntry, useReverseJournalEntry, useDeleteJournalEntry,
} from '@/hooks/useAccounting';
import {
  JOURNAL_STATUS_LABELS,
  type JournalEntry, type JournalEntryStatus,
} from '@/types/accounting';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { LoaderScreen } from '@/components/ui/LoaderScreen';
import { EmptyState } from '@/components/ui/EmptyState';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { JournalEntryModal } from '@/components/accounting/JournalEntryModal';
import { formatCurrency } from '@/lib/utils';

const STATUS_TONES: Record<JournalEntryStatus, 'neutral' | 'success' | 'danger'> = {
  draft:    'neutral',
  posted:   'success',
  reversed: 'danger',
};

export function JournalPage() {
  const { user } = useAuth();
  const canWrite = user ? can(user.role, 'accounting', 'write') : false;

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<'' | JournalEntryStatus>('');
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data, isLoading, error } = useJournal();
  const post = usePostJournalEntry();
  const reverse = useReverseJournalEntry();
  const del = useDeleteJournalEntry();

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<JournalEntry | null>(null);
  const [deleting, setDeleting] = useState<JournalEntry | null>(null);
  const [reversing, setReversing] = useState<JournalEntry | null>(null);
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.filter((e) => {
      if (status && e.status !== status) return false;
      if (search.trim()) {
        const s = search.toLowerCase();
        const hay = `${e.number} ${e.memo} ${e.reference ?? ''} ${e.sourceDocNumber ?? ''}`.toLowerCase();
        return hay.includes(s);
      }
      return true;
    });
  }, [data, search, status]);

  const stats = useMemo(() => {
    if (!data) return { drafts: 0, posted: 0, totalPosted: 0 };
    return {
      drafts: data.filter((e) => e.status === 'draft').length,
      posted: data.filter((e) => e.status === 'posted').length,
      totalPosted: data.filter((e) => e.status === 'posted').reduce((s, e) => s + e.totalDebit, 0),
    };
  }, [data]);

  if (isLoading) return <LoaderScreen />;
  if (error) {
    return (
      <Card>
        <EmptyState
          icon={AlertCircle}
          title="Could not load journal"
          description={(error as { message?: string })?.message ?? 'An unexpected error occurred.'}
        />
      </Card>
    );
  }

  async function onPost(e: JournalEntry) {
    try {
      await post.mutateAsync(e.id);
      toast.success(`Posted ${e.number}`);
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Post failed.');
    }
  }

  async function onConfirmReverse() {
    if (!reversing) return;
    try {
      const result = await reverse.mutateAsync(reversing.id);
      toast.success(`Reversed with ${result.number}`);
      setReversing(null);
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Reverse failed.');
    }
  }

  async function onConfirmDelete() {
    if (!deleting) return;
    try {
      await del.mutateAsync(deleting.id);
      toast.success(`Deleted ${deleting.number}`);
      setDeleting(null);
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Delete failed.');
    }
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-px bg-border rounded-sm overflow-hidden border border-border">
        <Stat label="Drafts"         value={String(stats.drafts)} alert={stats.drafts > 0} />
        <Stat label="Posted"         value={String(stats.posted)} />
        <Stat label="Posted Volume"  value={stats.totalPosted ? formatCurrency(stats.totalPosted) : '—'} accent />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[16rem]">
          <Input
            placeholder="Search by JE #, memo, reference…"
            leftIcon={<Search size={14} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="w-44">
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value as JournalEntryStatus | '')}
            options={[
              { value: '', label: 'All statuses' },
              { value: 'draft',    label: 'Draft' },
              { value: 'posted',   label: 'Posted' },
              { value: 'reversed', label: 'Reversed' },
            ]}
          />
        </div>
        {canWrite && (
          <Button leftIcon={<Plus size={14} />} onClick={() => setCreateOpen(true)}>
            New Entry
          </Button>
        )}
      </div>

      {/* Journal */}
      {filtered.length === 0 ? (
        <Card>
          <EmptyState
            icon={ListOrdered}
            title={search || status ? 'No entries match your filters' : 'No journal entries yet'}
            description={search || status ? 'Try clearing some filters.' : 'Start with a journal entry or let automated postings from invoices/payments populate the ledger.'}
            action={canWrite && !search && !status && (
              <Button leftIcon={<Plus size={14} />} onClick={() => setCreateOpen(true)}>New Entry</Button>
            )}
          />
        </Card>
      ) : (
        <Card className="p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="kicker px-6 py-3 font-normal">Entry</th>
                <th className="kicker px-6 py-3 font-normal">Date</th>
                <th className="kicker px-6 py-3 font-normal">Memo</th>
                <th className="kicker px-6 py-3 font-normal">Source</th>
                <th className="kicker px-6 py-3 font-normal">Status</th>
                <th className="kicker px-6 py-3 font-normal text-right">Amount</th>
                <th className="kicker px-6 py-3 font-normal w-12" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => (
                <EntryRow
                  key={e.id}
                  entry={e}
                  expanded={expanded === e.id}
                  onToggle={() => setExpanded(expanded === e.id ? null : e.id)}
                  canWrite={canWrite}
                  openMenu={openMenu === e.id}
                  onOpenMenu={() => setOpenMenu(openMenu === e.id ? null : e.id)}
                  onCloseMenu={() => setOpenMenu(null)}
                  onEdit={() => setEditing(e)}
                  onPost={() => onPost(e)}
                  onReverse={() => setReversing(e)}
                  onDelete={() => setDeleting(e)}
                />
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <JournalEntryModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <JournalEntryModal open={!!editing} onClose={() => setEditing(null)} entry={editing} />

      <ConfirmDialog
        open={!!reversing}
        onClose={() => setReversing(null)}
        onConfirm={onConfirmReverse}
        loading={reverse.isPending}
        destructive
        title={`Reverse ${reversing?.number}?`}
        confirmLabel="Create Reversing Entry"
        description="A new posted entry will be created with debits and credits swapped. This preserves the audit trail and properly corrects the books."
      />

      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={onConfirmDelete}
        loading={del.isPending}
        destructive
        title={`Delete ${deleting?.number}?`}
        confirmLabel="Delete draft"
        description="Only draft entries can be deleted. Posted entries must be reversed."
      />
    </div>
  );

  // STATUS_TONES reference (suppress TS unused warning)
  void STATUS_TONES;
}

function EntryRow({
  entry, expanded, onToggle, canWrite, openMenu, onOpenMenu, onCloseMenu,
  onEdit, onPost, onReverse, onDelete,
}: {
  entry: JournalEntry;
  expanded: boolean;
  onToggle: () => void;
  canWrite: boolean;
  openMenu: boolean;
  onOpenMenu: () => void;
  onCloseMenu: () => void;
  onEdit: () => void;
  onPost: () => void;
  onReverse: () => void;
  onDelete: () => void;
}) {
  return (
    <>
      <tr className="border-b border-border hover:bg-bg-hover/40 group cursor-pointer" onClick={onToggle}>
        <td className="px-6 py-4 font-mono text-brass-300">{entry.number}</td>
        <td className="px-6 py-4 text-text-muted tabular text-xs">
          {new Date(entry.date).toLocaleDateString('en-US', { dateStyle: 'medium' })}
        </td>
        <td className="px-6 py-4 text-text">
          {entry.memo}
          {entry.reference && <div className="text-2xs text-text-dim font-mono mt-0.5">Ref: {entry.reference}</div>}
        </td>
        <td className="px-6 py-4">
          {entry.source === 'manual'
            ? <span className="text-2xs uppercase tracking-ultra text-text-dim">Manual</span>
            : (
              <div className="text-2xs uppercase tracking-ultra text-brass-300">
                {entry.source}
                {entry.sourceDocNumber && <div className="font-mono normal-case tracking-normal text-text-dim">{entry.sourceDocNumber}</div>}
              </div>
            )}
        </td>
        <td className="px-6 py-4">
          <Badge tone={entry.status === 'posted' ? 'success' : entry.status === 'reversed' ? 'danger' : 'neutral'}>
            {JOURNAL_STATUS_LABELS[entry.status]}
          </Badge>
        </td>
        <td className="px-6 py-4 text-right tabular text-text">
          {formatCurrency(entry.totalDebit)}
        </td>
        <td className="px-6 py-4 text-right relative" onClick={(e) => e.stopPropagation()}>
          {canWrite && (
            <>
              <button
                onClick={onOpenMenu}
                className="p-1.5 text-text-dim hover:text-text hover:bg-bg-hover rounded-sm opacity-0 group-hover:opacity-100"
                aria-label="Actions"
              >
                <MoreVertical size={15} />
              </button>
              {openMenu && (
                <>
                  <div className="fixed inset-0 z-10" onClick={onCloseMenu} />
                  <div className="absolute right-4 top-12 z-20 w-44 bg-bg-raised border border-border-strong rounded-sm shadow-raised py-1 animate-fade-in">
                    {entry.status === 'draft' && (
                      <>
                        <button onClick={() => { onEdit(); onCloseMenu(); }}
                          className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-text hover:bg-bg-hover text-left">
                          <Pencil size={13} /> Edit
                        </button>
                        <button onClick={() => { onPost(); onCloseMenu(); }}
                          className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-success hover:bg-success/10 text-left">
                          <CheckCircle2 size={13} /> Post
                        </button>
                        <div className="my-1 border-t border-border" />
                        <button onClick={() => { onDelete(); onCloseMenu(); }}
                          className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-danger hover:bg-danger/10 text-left">
                          <Trash2 size={13} /> Delete
                        </button>
                      </>
                    )}
                    {entry.status === 'posted' && (
                      <button onClick={() => { onReverse(); onCloseMenu(); }}
                        className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-danger hover:bg-danger/10 text-left">
                        <RotateCcw size={13} /> Reverse
                      </button>
                    )}
                    {entry.status === 'reversed' && (
                      <div className="px-3 py-2 text-2xs text-text-dim">
                        No actions — entry reversed.
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </td>
      </tr>
      {expanded && (
        <tr className="bg-bg-subtle border-b border-border">
          <td colSpan={7} className="px-6 py-4">
            <div className="kicker mb-3">Lines · {entry.lines.length}</div>
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border">
                  <th className="kicker px-3 py-2 font-normal text-left w-32">Account</th>
                  <th className="kicker px-3 py-2 font-normal text-left">Description</th>
                  <th className="kicker px-3 py-2 font-normal text-right w-28">Debit</th>
                  <th className="kicker px-3 py-2 font-normal text-right w-28">Credit</th>
                </tr>
              </thead>
              <tbody>
                {entry.lines.map((l) => (
                  <tr key={l.id} className="border-b border-border last:border-0">
                    <td className="px-3 py-2 font-mono text-brass-300">{l.accountNumber}</td>
                    <td className="px-3 py-2 text-text-muted">
                      <span className="text-text">{l.accountName}</span>
                      {l.description && <span className="ml-2 text-text-dim">· {l.description}</span>}
                    </td>
                    <td className="px-3 py-2 text-right tabular">
                      {l.debit ? formatCurrency(l.debit) : <span className="text-text-dim">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right tabular">
                      {l.credit ? formatCurrency(l.credit) : <span className="text-text-dim">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-border-strong bg-bg-panel">
                  <td colSpan={2} className="px-3 py-2 text-right">
                    <span className="kicker">Totals</span>
                  </td>
                  <td className="px-3 py-2 text-right tabular font-medium text-text">{formatCurrency(entry.totalDebit)}</td>
                  <td className="px-3 py-2 text-right tabular font-medium text-text">{formatCurrency(entry.totalCredit)}</td>
                </tr>
              </tfoot>
            </table>
            {entry.createdByName && (
              <div className="mt-3 text-2xs text-text-dim">
                Created by {entry.createdByName}
                {entry.postedAt && ` · Posted ${new Date(entry.postedAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}`}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function Stat({ label, value, accent, alert }: { label: string; value: string; accent?: boolean; alert?: boolean }) {
  return (
    <div className="bg-bg-panel p-4">
      <div className="kicker mb-2">{label}</div>
      <div className={`font-display text-2xl tabular ${accent ? 'text-brass-300' : alert ? 'text-warning' : 'text-text'}`}>
        {value}
      </div>
    </div>
  );
}
